import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const Salas = () => {
    const [salas, setSalas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentSala, setCurrentSala] = useState({ name: '', ubication: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchSalas();
    }, []);

    const fetchSalas = async () => {
        setIsLoading(true);
        try {
            const response = await api.get('/salas');
            setSalas(response.data);
        } catch (error) {
            console.error("Error fetching salas", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await api.put(`/salas/${currentSala.id}`, currentSala);
            } else {
                await api.post('/salas', currentSala);
            }
            fetchSalas();
            closeModal();
        } catch (error) {
            console.error("Error saving sala", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de eliminar esta sala? Todos los usuarios y asistencias asociados perderán la referencia de la sala.")) {
            try {
                await api.delete(`/salas/${id}`);
                fetchSalas();
            } catch (error) {
                console.error("Error deleting sala", error);
            }
        }
    };

    const openModal = (sala = null) => {
        if (sala) {
            setCurrentSala(sala);
            setIsEditing(true);
        } else {
            setCurrentSala({ name: '', ubication: '' });
            setIsEditing(false);
        }
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);

    const filteredSalas = salas.filter(sala => 
        sala.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (sala.ubication && sala.ubication.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div className="animate-fadeIn">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                <div>
                    <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">Gestión de Salas (Salones)</h1>
                    <p className="text-slate-500 mt-2">Crea y administra las salas físicas del evento para el registro de staff y asignación de actividades.</p>
                </div>
                <div className="flex w-full md:w-auto gap-4">
                    <div className="relative flex-grow">
                        <span className="absolute inset-y-0 left-4 flex items-center text-slate-400 font-bold">🔍</span>
                        <input 
                            type="text"
                            placeholder="Buscar sala por nombre o ubicación..."
                            className="bg-white border border-slate-100 pl-12 pr-6 py-3.5 rounded-2xl w-full focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-sm"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <button 
                        onClick={() => openModal()}
                        className="bg-blue-600 text-white px-8 py-3.5 rounded-2xl font-bold shadow-lg shadow-blue-900/20 hover:bg-blue-500 transition-all flex items-center whitespace-nowrap"
                    >
                        + Nueva Sala
                    </button>
                </div>
            </header>

            {isLoading ? (
                <div className="text-center py-20 bg-white rounded-[40px] border border-slate-100">
                    <div className="w-10 h-10 border-4 border-slate-100 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-400 font-medium">Cargando salas...</p>
                </div>
            ) : filteredSalas.length === 0 ? (
                <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 p-20 text-center animate-fadeIn">
                    <div className="text-6xl mb-6 opacity-20">🏢</div>
                    <h3 className="text-xl font-bold text-slate-800">No se encontraron salas</h3>
                    <p className="text-slate-400 mt-2">Registra una nueva sala física para comenzar.</p>
                </div>
            ) : (
                <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Nombre de la Sala</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Ubicación / Detalles</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Fecha de Creación</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {filteredSalas.map((sala) => (
                                <tr key={sala.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="p-6">
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-black mr-4 border border-slate-200">
                                                🏢
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-900 leading-snug">
                                                    {sala.name}
                                                </div>
                                                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">ID: {sala.id}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        <div className="text-sm font-semibold text-slate-700">{sala.ubication || 'Sin ubicación registrada'}</div>
                                    </td>
                                    <td className="p-6">
                                        <div className="text-sm text-slate-500">{new Date(sala.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                                    </td>
                                    <td className="p-6 text-right">
                                        <div className="flex justify-end space-x-2">
                                            <button 
                                                onClick={() => openModal(sala)}
                                                className="p-3 rounded-xl bg-slate-100 text-slate-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                                                title="Editar"
                                            >
                                                ✏️
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(sala.id)}
                                                className="p-3 rounded-xl bg-slate-100 text-red-400 hover:bg-red-500 hover:text-white transition-all shadow-sm"
                                                title="Eliminar"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md flex items-center justify-center p-6 z-[100] animate-fadeIn">
                    <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-xl overflow-hidden animate-slideUp">
                        <div className="bg-gradient-to-r from-blue-700 to-indigo-600 p-8 text-white relative">
                            <h2 className="text-3xl font-black">{isEditing ? 'Editar Sala' : 'Nueva Sala'}</h2>
                            <p className="text-blue-100 mt-1">Ingresa los datos para registrar la sala del evento.</p>
                            <button onClick={closeModal} className="absolute top-8 right-8 text-white/50 hover:text-white text-2xl">✕</button>
                        </div>
                        <form onSubmit={handleSave} className="p-10 flex flex-col gap-6">
                            <div>
                                <label className="block text-slate-700 text-sm font-bold mb-2">Nombre de la Sala</label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                    value={currentSala.name}
                                    onChange={e => setCurrentSala({...currentSala, name: e.target.value})}
                                    required
                                    placeholder="Ej. Salón Principal A, Auditorio Norte"
                                />
                            </div>

                            <div>
                                <label className="block text-slate-700 text-sm font-bold mb-2">Ubicación / Detalles</label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                    value={currentSala.ubication}
                                    onChange={e => setCurrentSala({...currentSala, ubication: e.target.value})}
                                    placeholder="Ej. Piso 2, Costado Oeste"
                                />
                            </div>

                            <div className="pt-6 flex justify-end space-x-4">
                                <button type="button" onClick={closeModal} className="px-8 py-4 text-slate-500 font-bold hover:text-slate-800 transition-colors">Cancelar</button>
                                <button type="submit" className="bg-blue-600 text-white px-12 py-4 rounded-3xl font-black shadow-xl shadow-blue-900/20 hover:bg-blue-500 transition-all active:scale-95">
                                    {isEditing ? 'Guardar Cambios' : 'Registrar Sala'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Salas;
